import React, { useState, useCallback, useRef } from 'react';
import {
  Typography, Box, Paper
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import ActionSettingsLayout from '../ActionSettingsHeader';
import { useActionSettings } from '../../hooks/useActionSettings';
import TagEditor from '../Widgets/TagEditor';

const ActionsCreateTagBrowseWeb = ({ caseid, tags, settingId, onSaveStatusChange }) => {
  const { t } = useTranslation();

  // Fields for "createtagbrowseweb"
  const [startUrl, setStartUrl] = useState('');
  const [instruction, setInstruction] = useState('');
  const [tagField, setTagField] = useState('');

  const startUrlRef = useRef(null);
  const instructionRef = useRef(null);
  const tagFieldRef = useRef(null);

  // Unified action settings hook
  const {
    isEnabled,
    setIsEnabled,
    conditions,
    isSaveDisabled,
    forceRunLoading,
    forceRunResult,
    popUpOpen,
    forceRunWarningOpen,
    forceRunWarningCount,
    handleSave,
    handleForceRun,
    handleForceRunConfirm,
    handleForceRunCancel,
    handleConditionsChange,
    handleCloseStatusWindow
  } = useActionSettings({
    settingId,
    caseid,
    actionType: 'createtagbrowseweb',
    onSaveStatusChange,
    customComparison: (initial) => {
      return startUrl === (initial.startUrl || '') &&
             instruction === (initial.instruction || '') &&
             tagField === (initial.tagField || '');
    },
    customSaveData: () => ({
      startUrl,
      instruction,
      tagField
    }),
    loadDataFunction: (data) => {
      // The hook passes 'data' which usually contains customData
      const customData = data.customData || {};
      setStartUrl(customData.startUrl || '');
      setInstruction(customData.instruction || '');
      setTagField(customData.tagField || '');
    }
  });

  return (
    <ActionSettingsLayout
      title={t('settings.createtagbrowseweb.title')}
      isEnabled={isEnabled}
      onEnabledChange={setIsEnabled}
      isSaveDisabled={isSaveDisabled}
      onSave={handleSave}
      onForceRun={handleForceRun}
      forceRunLoading={forceRunLoading}
      forceRunResult={forceRunResult}
      popUpOpen={popUpOpen}
      onCloseStatusWindow={handleCloseStatusWindow}
      forceRunWarningOpen={forceRunWarningOpen}
      forceRunWarningCount={forceRunWarningCount}
      onForceRunConfirm={handleForceRunConfirm}
      onForceRunCancel={handleForceRunCancel}
      conditions={conditions}
      onConditionsChange={handleConditionsChange}
      tags={tags}
      tagsDropdownProps={{
        tags: tags,
        // We rely on TagEditor's internal handling now, so we don't need external handlers here
        // But ActionSettingsLayout might expect these if we use the header menu. 
        // For now, let's leave them null or minimal if not used.
      }}
    >
      {/* Main Configuration Section */}
      <Box sx={{ mt: 0 }}>
        <Typography variant="overline" display="block" gutterBottom>
          {t('settings.createtagbrowseweb.configurationSectionTitle', 'Configuration')}
        </Typography>
        
        <Box sx={{ mb: 2 }}>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, mb: 0.5, display: 'block' }}>
                {t('settings.createtagbrowseweb.startUrl')}
            </Typography>
            <Box sx={{
                border: '1px solid #d0d0e8',
                borderRadius: '12px',
                backgroundColor: '#fff',
                overflow: 'hidden',
                '&:hover': { borderColor: '#5757b0' },
            }}>
                <TagEditor
                    caseid={caseid}
                    ref={startUrlRef}
                    value={startUrl}
                    onChange={setStartUrl}
                    tags={tags}
                    placeholder="https://example.com"
                    singleLine
                />
            </Box>
        </Box>

        {/* Updated Instruction Field */}
        <Typography variant="subtitle2" gutterBottom sx={{ color: '#555', fontWeight: 600, mb: 0.5, mt: 2 }}>
          {t('settings.createtagbrowseweb.instruction', 'Instruction')}
        </Typography>
        
        <Box sx={{
            border: '1px solid #d0d0e8',
            borderRadius: '12px',
            backgroundColor: '#f8f9fa',
            overflow: 'hidden',
            '&:hover': { borderColor: '#5757b0' },
            minHeight: '100px'
        }}>
             <TagEditor
                    caseid={caseid}
                ref={instructionRef}
                value={instruction}
                onChange={setInstruction}
                tags={tags}
                placeholder={t('settings.createtagbrowseweb.instructionPlaceholder', 'Enter instruction detailing what to extract or how to browse...')}
                multiline
                minRows={3}
             />
        </Box>
        
        <Box sx={{ mb: 2, mt: 2 }}>
             <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, mb: 0.5, display: 'block' }}>
                {t('settings.createtagbrowseweb.tagField')}
             </Typography>
             <Box sx={{
                border: '1px solid #d0d0e8',
                borderRadius: '12px',
                backgroundColor: '#fff',
                overflow: 'hidden',
                '&:hover': { borderColor: '#5757b0' },
            }}>
                <TagEditor
                    caseid={caseid}
                    ref={tagFieldRef}
                    value={tagField}
                    onChange={setTagField}
                    tags={tags}
                    fullWidth
                    placeholder={t('settings.createtagbrowseweb.tagFieldHelper')}
                    singleLine
                />
            </Box>
        </Box>
      </Box>
    </ActionSettingsLayout>
  );
};

export default React.memo(ActionsCreateTagBrowseWeb);
